import { Course } from '../types';

/**
 * Get today's date string (YYYY-MM-DD) in a given IANA timezone.
 * Falls back to UTC if the timezone is invalid or missing.
 */
function getTodayInTimezone(timezone?: string): string {
  const now = new Date();
  try {
    // Intl.DateTimeFormat gives us the calendar date in the target timezone
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone || 'UTC',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(now);

    const y = parts.find(p => p.type === 'year')!.value;
    const m = parts.find(p => p.type === 'month')!.value;
    const d = parts.find(p => p.type === 'day')!.value;
    return `${y}-${m}-${d}`;
  } catch {
    // Invalid timezone — fall back to UTC
    return now.toISOString().split('T')[0];
  }
}

/**
 * Check if a course is reservable (start date is strictly in the future
 * according to the course's own timezone).
 */
export function isCourseReservable(course: Course): boolean {
  if (!course.startDate) return false;
  const startDateStr = course.startDate.split('T')[0]; // normalize to YYYY-MM-DD
  const todayStr = getTodayInTimezone(course.startDateTimezone);
  return startDateStr > todayStr;
}

/**
 * Format the course start date for display, using the course's timezone.
 */
export function formatCourseStartDate(course: Course): string {
  if (!course.startDate) return '';
  const dateStr = course.startDate.split('T')[0];
  // Parse as local date parts to avoid UTC shift
  const [y, m, d] = dateStr.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  const formatted = date.toLocaleDateString();
  if (course.startDateTimezone) {
    // Show abbreviated timezone
    try {
      const tzShort = new Intl.DateTimeFormat('en-US', {
        timeZone: course.startDateTimezone,
        timeZoneName: 'short',
      }).formatToParts(date).find(p => p.type === 'timeZoneName')?.value;
      if (tzShort) return `${formatted} (${tzShort})`;
    } catch {
      // ignore
    }
  }
  return formatted;
}
